import React, { FC, useMemo, useRef } from 'react';
import {
  Button,
  Center,
  Flex,
  Heading,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { UserPill } from 'components/core/UserPill';
import { DataTable } from 'components/modules/DataTable';
import { WashhubInventoryViewProps } from './WashhubInventory.props';
import { BsClockHistory } from 'react-icons/bs';
import { AddWashhubInventoryModal } from 'components/modules/AddWashhubInventoryModal';
import { RefType } from 'components/modules/AddWashhubInventoryModal/AddWashhubInventoryModal.props';
import { EditRefType } from 'components/modules/EditWashhubInventoryModal/EditWashhubInventoryModal.props';
import { WashhubInventory as hubInventoryType } from 'types/washhub';
import { EditWashhubInventoryModal } from 'components/modules/EditWashhubInventoryModal';
import { deleteHubInventoryCount } from 'api/washhub';
import { useQueryClient } from 'react-query';
import { washHub } from 'store';
import { exportHubInventoryCount } from 'api/washhub';
import { useMutation } from 'react-query';
import { buildWashhubInventoryColumns } from './washhubInventoryColumns';

const WashhubInventoryView: FC<WashhubInventoryViewProps> = (props) => {
  const toast = useToast();
  const { WashhubInventory, isLoading, pagination } = props;
  const selectedWashHub = washHub.selectors.useSelectedWashHub();
  const washHubId = selectedWashHub?._id || '';

  const csvRef = useRef<any>(null);

  const addWashhubInventoryRef = useRef<RefType>(null);
  const editWashhubInventoryRef = useRef<EditRefType>(null);
  const queryClient = useQueryClient();

  const invalidateQuery = () => {
    queryClient.invalidateQueries('fetch-hub-inventory-count');
  };

  const handleDelete = async (count: hubInventoryType) => {
    await deleteHubInventoryCount(count._id);
    toast({
      status: 'success',
      title: 'Success',
      description: 'Hub Inventory Count Deleted',
      isClosable: true,
      duration: 5000,
    });
    invalidateQuery();
  };

  const columns = useMemo(() => buildWashhubInventoryColumns(handleDelete), [
    handleDelete,
  ]);

  const { mutate, data: csvData = '', isLoading: isExporting } = useMutation(
    async () => {
      let response: string = '';

      response = await exportHubInventoryCount({
        washHub: washHubId,
      });

      return response;
    },
    {
      onSuccess: () => {
        setTimeout(() => {
          csvRef.current?.click();
        }, 500);
      },
    },
  );

  const renderExpBtn = () => {
    return (
      <>
        <Button
          isLoading={isExporting}
          ml={2}
          minW="200px"
          onClick={() => {
            mutate();
          }}>
          Export Hub Inventory CSV
        </Button>
        {Boolean(csvData) && (
          <a
            href={`data:text/csv;charset=utf-8,${encodeURIComponent(csvData)}`}
            download={`washhub-${selectedWashHub?.name}-inventory-count.csv`}
            style={{ display: 'none' }}
            ref={csvRef}>
            Export CSV
          </a>
        )}
      </>
    );
  };

  return (
    <Flex flex={1} direction="column" color="support.500" overflow="auto">
      <Flex align="flex-start" justify="space-between">
        <Heading mb={6} fontSize="24px">
          Hub Inventory Count
        </Heading>
        <Flex align="flex-end" gap={4}>
          {renderExpBtn()}
          <Button
            isDisabled={washHubId === ''}
            minW="200px"
            leftIcon={<BsClockHistory size="20px" />}
            onClick={() => {
              addWashhubInventoryRef?.current?.onOpen?.();
            }}>
            Create Hub Inventory
          </Button>
          <Stack isInline>
            <UserPill />
          </Stack>
        </Flex>
      </Flex>

      <DataTable<hubInventoryType>
        mt="50px"
        minW="1300px"
        data={WashhubInventory}
        columns={columns}
        isLoading={isLoading}
        pagination={pagination}
        onRowClick={(count) => {
          editWashhubInventoryRef?.current?.onOpen?.(count);
        }}
        renderEmpty={() => (
          <Center p={8}>
            <Text fontSize="20px">No Hub Inventory Count available</Text>
          </Center>
        )}
      />
      <AddWashhubInventoryModal ref={addWashhubInventoryRef} />
      <EditWashhubInventoryModal ref={editWashhubInventoryRef} />
    </Flex>
  );
};

export default WashhubInventoryView;
